import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Upload, Image as ImageIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RelicsCardsManager = () => {
  const [cards, setCards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSeason, setFilterSeason] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    card_type: '',
    image_url: '',
    back_image_url: '',
    alt_text: '',
    category_id: '',
    season: 1,
    is_active: true,
    display_order: 0
  });

  useEffect(() => {
    fetchCategories();
    fetchCards();
  }, [filterCategory, filterSeason]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin/relics/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchCards = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (filterCategory) params.append('category_id', filterCategory);
      if (filterSeason) params.append('season', filterSeason);
      
      const response = await axios.get(`${API_URL}/api/admin/relics/cards?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCards(response.data);
    } catch (error) {
      console.error('Error fetching cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, fieldName = 'image_url') => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload`, uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData({ ...formData, [fieldName]: response.data.url });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      if (editingCard) {
        await axios.patch(
          `${API_URL}/api/admin/relics/cards/${editingCard.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(`${API_URL}/api/admin/relics/cards`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setShowModal(false);
      setEditingCard(null);
      setFormData({
        title: '',
        card_type: '',
        image_url: '',
        back_image_url: '',
        alt_text: '',
        category_id: '',
        season: 1,
        is_active: true,
        display_order: 0
      });
      fetchCards();
    } catch (error) {
      console.error('Error saving card:', error);
      alert('Failed to save card');
    }
  };

  const handleDelete = async (cardId) => {
    if (!window.confirm('Delete this card?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API_URL}/api/admin/relics/cards/${cardId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCards();
    } catch (error) {
      console.error('Error deleting card:', error);
      alert('Failed to delete card');
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Relic Cards</h1>
        <button
          onClick={() => {
            setEditingCard(null);
            setFormData({
              title: '',
              card_type: '',
              image_url: '',
              alt_text: '',
              category_id: '',
              season: 1,
              is_active: true,
              display_order: 0
            });
            setShowModal(true);
          }}
          className="flex items-center space-x-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600"
        >
          <Plus className="w-4 h-4" />
          <span>New Card</span>
        </button>
      </div>

      <div className="bg-white border rounded-lg p-4 mb-6">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Category</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Season</label>
            <select
              value={filterSeason}
              onChange={(e) => setFilterSeason(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">All Seasons</option>
              <option value="1">Season 1</option>
              <option value="2">Season 2</option>
              <option value="3">Season 3</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setFilterCategory('');
                setFilterSeason('');
              }}
              className="w-full px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 gap-3">
        {cards.length === 0 ? (
          <div className="col-span-full p-8 text-center text-gray-500">
            No cards found
          </div>
        ) : (
          cards.map((card) => (
            <div key={card.id} className="bg-white border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div className="aspect-[3/4] bg-gray-200">
                {card.image_url ? (
                  <img
                    src={card.image_url}
                    alt={card.alt_text}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <ImageIcon className="w-8 h-8" />
                  </div>
                )}
              </div>
              <div className="p-2">
                <p className="text-xs font-semibold text-gray-900 truncate" title={card.title}>{card.title}</p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs text-gray-500">S{card.season}</span>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => {
                        setEditingCard(card);
                        setFormData({
                          title: card.title,
                          card_type: card.card_type,
                          image_url: card.image_url,
                          back_image_url: card.back_image_url || '',
                          alt_text: card.alt_text,
                          category_id: card.category_id,
                          season: card.season,
                          is_active: card.is_active,
                          display_order: card.display_order
                        });
                        setShowModal(true);
                      }}
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit"
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleDelete(card.id)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingCard ? 'Edit Card' : 'New Card'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Card Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="Demonic Tutor"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Card Type *</label>
                  <select
                    value={formData.card_type}
                    onChange={(e) => setFormData({ ...formData, card_type: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">Select type...</option>
                    <option value="Creature">Creature</option>
                    <option value="Instant">Instant</option>
                    <option value="Sorcery">Sorcery</option>
                    <option value="Artifact">Artifact</option>
                    <option value="Enchantment">Enchantment</option>
                    <option value="Planeswalker">Planeswalker</option>
                    <option value="Land">Land</option>
                    <option value="Battle">Battle</option>
                    <option value="Plane">Plane</option>
                    <option value="Tribal">Tribal</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Card Image (Front) *</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'image_url')}
                  className="hidden"
                  id="card-image-upload"
                />
                <label
                  htmlFor="card-image-upload"
                  className="inline-flex items-center space-x-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50"
                >
                  <Upload className="w-4 h-4" />
                  <span>{uploading ? 'Uploading...' : 'Upload Front'}</span>
                </label>
                {formData.image_url && (
                  <div className="mt-2">
                    <img src={formData.image_url} alt="Front Preview" className="w-32 rounded border" />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Card Image (Back) - Optional</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'back_image_url')}
                  className="hidden"
                  id="card-back-image-upload"
                />
                <label
                  htmlFor="card-back-image-upload"
                  className="inline-flex items-center space-x-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50"
                >
                  <Upload className="w-4 h-4" />
                  <span>{uploading ? 'Uploading...' : 'Upload Back'}</span>
                </label>
                {formData.back_image_url && (
                  <div className="mt-2">
                    <img src={formData.back_image_url} alt="Back Preview" className="w-32 rounded border" />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Alt Text (SEO) *</label>
                <input
                  type="text"
                  value={formData.alt_text}
                  onChange={(e) => setFormData({ ...formData, alt_text: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Demonic Tutor MTG card art"
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Category *</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  >
                    <option value="">Select...</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Season</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.season}
                    onChange={(e) => setFormData({ ...formData, season: parseInt(e.target.value) })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Display Order</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                    className="w-full border rounded-lg px-3 py-2"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded"
                />
                <label className="text-sm font-medium">Active (show on public page)</label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCard(null);
                  }}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                >
                  {editingCard ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
